/**
 * Stage 4 shapes — AI screening call (Retell) + code qualification
 * (docs/BUILD_PLAN.md → Stage 4 contract). The worker owns the truth; these
 * mirror `GET /api/leads/:id/screening`, `GET/PUT /api/settings/screening`.
 */
import type { Qualification } from '@/features/pipeline/types';

export type ScreeningStatus =
  | 'scheduled'
  | 'dialing'
  | 'in_progress'
  | 'completed'
  | 'no_answer'
  | 'voicemail'
  | 'failed'
  | 'hung_up';

/** Statuses during which the UI polls and the card shows a phone glyph. */
export const ACTIVE_SCREENING_STATUSES: ReadonlySet<ScreeningStatus> = new Set(['scheduled', 'dialing', 'in_progress']);

export function isActiveScreening(status: ScreeningStatus | null | undefined): boolean {
  return !!status && ACTIVE_SCREENING_STATUSES.has(status);
}

/** One row of `screening_calls`. */
export interface ScreeningCall {
  id: string;
  attempt_no: number;
  status: ScreeningStatus;
  scheduled_for: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_s: number | null;
  disconnect_reason: string | null;
  transcript: string | null;
  recording_url: string | null;
  extracted_json: Record<string, unknown> | string | null;
  qualification: Qualification;
  qualification_reason: string | null;
  created_at: string;
}

/** `GET /api/leads/:id/screening`. */
export interface LeadScreening {
  config_draft: boolean;
  attempts: ScreeningCall[];
  latest: ScreeningCall | null;
}

/** `lead.screening` on `GET /api/leads/:id` (null when no call was ever scheduled). */
export interface ScreeningSummary {
  latest_status: ScreeningStatus | null;
  attempts: number;
  qualification: Qualification;
  qualification_reason: string | null;
}

/** `lead.ai_fields.screening` — what the last analysed call produced. */
export interface ScreeningFields {
  answers: Record<string, unknown>;
  result: Exclude<Qualification, null> | null;
  score: number | null;
  /** The denominator the worker scored against (Stage 4 QA carry-over); `null` on older rows. */
  max_score: number | null;
  reasons: string[];
  call_id: string | null;
}

export type QuestionType = 'text' | 'number' | 'boolean' | 'choice';

export interface ScreeningQuestion {
  key: string;
  prompt: string;
  type: QuestionType;
  choices?: string[];
  required: boolean;
}

export type RuleOp = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'contains' | 'truthy' | 'falsy';

export const RULE_OPS: ReadonlyArray<{ value: RuleOp; label: string; needsValue: boolean }> = [
  { value: 'eq', label: 'equals', needsValue: true },
  { value: 'neq', label: 'does not equal', needsValue: true },
  { value: 'gt', label: 'is more than', needsValue: true },
  { value: 'gte', label: 'is at least', needsValue: true },
  { value: 'lt', label: 'is less than', needsValue: true },
  { value: 'lte', label: 'is at most', needsValue: true },
  { value: 'in', label: 'is one of', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'truthy', label: 'is yes / present', needsValue: false },
  { value: 'falsy', label: 'is no / missing', needsValue: false },
];

export type RuleValue = string | number | boolean | Array<string | number | boolean> | null;

export interface KnockoutRule {
  field: string;
  op: RuleOp;
  value: RuleValue;
  reason: string;
}

export interface ScoringRule {
  field: string;
  op: RuleOp;
  value: RuleValue;
  points: number;
}

export interface ScreeningRules {
  knockouts: KnockoutRule[];
  scoring: ScoringRule[];
  qualified_min: number;
  review_min: number;
}

export interface ScreeningConfig {
  draft: boolean;
  questions: ScreeningQuestion[];
  rules: ScreeningRules;
}

/** `GET /api/settings/screening` → one config per role slug. */
export type ScreeningConfigs = Record<string, ScreeningConfig>;

export const EMPTY_CONFIG: ScreeningConfig = {
  draft: true,
  questions: [],
  rules: { knockouts: [], scoring: [], qualified_min: 0, review_min: 0 },
};
